import { useCallback, useEffect, useState } from 'react';
import { Chapter } from '@/types';

interface PullToUnlockArgs {
  chapters: Chapter[];
  isChapterUnlocked: (chapter: Chapter) => boolean;
  user: { id: string; tokenBalance: number } | null;
  signIn: () => void;
  onUnlock: (chapter: Chapter) => Promise<boolean>;
}

interface PullToUnlockResult {
  pullProgress: number;
  resetPullProgress: () => void;
}

export function usePullToUnlock({
  chapters,
  isChapterUnlocked,
  user,
  signIn,
  onUnlock,
}: PullToUnlockArgs): PullToUnlockResult {
  const [pullProgress, setPullProgress] = useState(0);

  const resetPullProgress = useCallback(() => {
    setPullProgress(0);
  }, []);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const nextLocked = chapters.find((ch) => !isChapterUnlocked(ch) && ch.tokenCost > 0);
      if (!nextLocked) return;

      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const atBottom = scrollTop >= docHeight - 20;

      if (atBottom && e.deltaY > 0) {
        e.preventDefault();
        setPullProgress((prev) => {
          const newProgress = Math.min(prev + (e.deltaY / 500), 1);
          if (newProgress >= 1 && prev < 1) {
            if (user && user.tokenBalance !== undefined) {
              void onUnlock(nextLocked).then((success) => {
                if (success) {
                  resetPullProgress();
                }
              });
            } else {
              resetPullProgress();
              signIn();
            }
          }
          return newProgress;
        });
      }
    };

    const nextLocked = chapters.find((ch) => !isChapterUnlocked(ch) && ch.tokenCost > 0);
    if (nextLocked) {
      window.addEventListener('wheel', handleWheel, { passive: false });
      return () => window.removeEventListener('wheel', handleWheel);
    }
  }, [chapters, isChapterUnlocked, onUnlock, resetPullProgress, signIn, user]);

  return { pullProgress, resetPullProgress };
}
